import { useEffect, useState } from "react";
import { Trash2, Plus, Info } from "lucide-react";
import CreateClassModal from "../../modals/CreateClassModal";
import ConfirmDeleteClassModal from "../../modals/ConfirmDeleteClassModal";
import { createClass } from "../../../utils/createClass";
import { toast } from "react-toastify";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";

interface ClassRow {
  id: string;
  name: string;
  semester: string;
  description?: string;
  grade_type: string;
  settings: { class_time: string };
  created_at: string;
  teacherName?: string;
}

export default function AdminClasses() {
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDistrictLoading, setIsDistrictLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<ClassRow | null>(null);
  const { profile } = useUserStore();
  const [district, setDistrict] = useState<{ id: string; name: string } | null>(null);

  async function fetchDistrict() {
    if (!profile?.district) {
      setIsDistrictLoading(false);
      return;
    }
    setIsDistrictLoading(true);
    const { data, error } = await supabase
      .from("districts")
      .select("id, name")
      .eq("id", profile.district)
      .maybeSingle();
    if (error) console.error("Failed to fetch district:", error);
    setDistrict(data);
    setIsDistrictLoading(false);
  }

  async function fetchClasses(search?: string) {
    if (!profile?.district) {
      setClasses([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    // Resolve teacher IDs in this district via schools
    const { data: schools, error: schoolsErr } = await supabase
      .from("schools")
      .select("id")
      .eq("district_id", profile.district);

    if (schoolsErr || !schools?.length) {
      setClasses([]);
      setIsLoading(false);
      return;
    }

    const schoolIds = schools.map((s) => s.id);

    const { data: teacherProfiles, error: teachersErr } = await supabase
      .from("profiles")
      .select("id, firstname, lastname")
      .in("school", schoolIds)
      .eq("role", "teacher");

    if (teachersErr) {
      console.error("Failed to fetch teachers:", teachersErr);
      setClasses([]);
      setIsLoading(false);
      return;
    }

    // Also include classes owned by the admin themselves
    const teacherMap: Record<string, string> = {};
    const teacherIds = (teacherProfiles ?? []).map((t) => {
      teacherMap[t.id] = `${t.firstname} ${t.lastname}`;
      return t.id;
    });

    // Include admin's own user ID
    const { data: adminProfile } = await supabase
      .from("profiles")
      .select("id, firstname, lastname")
      .eq("id", profile.id ?? "")
      .maybeSingle();

    if (adminProfile) {
      teacherIds.push(adminProfile.id);
      teacherMap[adminProfile.id] = `${adminProfile.firstname} ${adminProfile.lastname} (admin)`;
    }

    if (!teacherIds.length) {
      setClasses([]);
      setIsLoading(false);
      return;
    }

    let query = supabase
      .from("classes")
      .select("id, name, semester, description, grade_type, settings, created_at, user_id")
      .in("user_id", teacherIds)
      .order("created_at", { ascending: false });

    if (search) {
      query = query.ilike("name", `%${search}%`);
    }

    const { data, error } = await query;
    if (error) {
      console.error("Failed to fetch classes:", error);
      setIsLoading(false);
      return;
    }

    const mapped = (data ?? []).map((c) => ({
      ...c,
      teacherName: teacherMap[c.user_id] ?? "Unknown",
    }));

    setClasses(mapped);
    setIsLoading(false);
  }

  useEffect(() => {
    fetchDistrict();
  }, [profile]);

  useEffect(() => {
    const id = setTimeout(() => fetchClasses(searchTerm), searchTerm ? 400 : 0);
    return () => clearTimeout(id);
  }, [searchTerm, profile?.district]);

  const handleCreate = async (data: Parameters<typeof createClass>[0]) => {
    const result = await createClass(data);
    if (!result.error) {
      toast.success("Class created successfully!");
      fetchClasses(searchTerm);
    } else {
      toast.error("Failed to create class: " + result.error);
    }
    return result;
  };

  const handleDeleteClick = (cls: ClassRow) => {
    setSelectedClass(cls);
    setIsDeleteOpen(true);
  };

  return (
    <div>
      <CreateClassModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreate}
      />

      <ConfirmDeleteClassModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedClass(null);
        }}
        cls={selectedClass as any}
        onClassDeleted={() => {
          setIsDeleteOpen(false);
          setSelectedClass(null);
          fetchClasses(searchTerm);
        }}
      />

      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Classes</h1>
          <p className="text-sm text-gray-600 mt-1">
            District:{" "}
            {isDistrictLoading
              ? "Loading..."
              : district?.name || "Not assigned"}
          </p>
          {!district?.name && !isDistrictLoading && (
            <p className="text-sm text-yellow-600 mt-1 flex items-center gap-1">
              <Info className="w-4 h-4" />
              Please contact Project Explore <b>Super Admin</b> to get assigned
              to a district.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsCreateOpen(true)}
            disabled={!district?.name}
            className="btn btn-small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Class
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Classes
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by class name"
                value={searchTerm}
                disabled={!district?.name || isDistrictLoading}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teacher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Semester
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading classes...
                    </td>
                  </tr>
                ) : classes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No classes found matching your search."
                        : "No classes found in this district."}
                    </td>
                  </tr>
                ) : (
                  classes.map((cls) => (
                    <tr key={cls.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {cls.name}
                        </div>
                        {cls.description && (
                          <div className="text-xs text-gray-400">
                            {cls.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {cls.teacherName}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {cls.semester}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {new Date(cls.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleDeleteClick(cls)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Delete Class"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
